import React, { useState, useRef } from "react";
import TimeSelect from "components/admin/TimeSelect";
import * as S from "./AdminBuildingDrawer.styled";
import { adminBuildingService, OperationTimeInput } from "services/adminBuildingService";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  label: string;
  latitude: string;
  longitude: string;
}

interface FormErrors {
  name?: string;
  label?: string;
  latitude?: string;
  longitude?: string;
  thumbnail?: string;
  operationTimes?: string;
}

const emptyOpTime = (): OperationTimeInput => ({
  name: "",
  startTime: "",
  endTime: "",
});

/** "HH:MM" → "HH:MM:SS" */
const toTimeString = (hhmm: string) => (hhmm ? `${hhmm}:00` : "");

const AdminBuildingDrawer: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState<FormValues>({
    name: "", label: "", latitude: "", longitude: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [opTimes, setOpTimes] = useState<OperationTimeInput[]>([emptyOpTime()]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const extraInputRef = useRef<HTMLInputElement>(null);

  /* ── 폼 변경 ── */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  /* ── 썸네일 선택 ── */
  const handleThumbnail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setThumbnailFile(file);
    setErrors((prev) => ({ ...prev, thumbnail: undefined }));
  };

  /* ── 추가 이미지 선택 ── */
  const handleExtraFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setExtraFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeExtraFile = (index: number) => {
    setExtraFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── 운영 시간 ── */
  const handleOpTimeChange = (
    index: number,
    field: keyof OperationTimeInput,
    value: string
  ) => {
    setOpTimes((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setErrors((prev) => ({ ...prev, operationTimes: undefined }));
  };

  const addOpTime = () => setOpTimes((prev) => [...prev, emptyOpTime()]);
  const removeOpTime = (index: number) =>
    setOpTimes((prev) => prev.filter((_, i) => i !== index));

  /* ── 유효성 검사 ── */
  const validate = (): boolean => {
    const next: FormErrors = {};

    if (!form.name.trim()) next.name = "건물명을 입력해주세요.";
    if (!form.label.trim()) next.label = "약칭을 입력해주세요.";
    if (!form.latitude || isNaN(Number(form.latitude)))
      next.latitude = "올바른 위도를 입력해주세요.";
    if (!form.longitude || isNaN(Number(form.longitude)))
      next.longitude = "올바른 경도를 입력해주세요.";
    if (!thumbnailFile) next.thumbnail = "썸네일 이미지를 선택해주세요.";

    const invalidOp = opTimes.some(
      (t) => !t.name.trim() || !t.startTime || !t.endTime
    );
    if (invalidOp) next.operationTimes = "모든 운영 시간 항목을 입력해주세요.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* ── 제출 ── */
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      // 1. 썸네일 업로드
      const thumbnailUrl = await adminBuildingService.uploadImage(thumbnailFile!);

      // 2. 추가 이미지 업로드 (병렬)
      const imageUrls = await Promise.all(
        extraFiles.map((f) => adminBuildingService.uploadImage(f))
      );

      // 3. 건물 생성
      const universityId = Number(localStorage.getItem("adminUniversityId"));
      await adminBuildingService.create(universityId, {
        name: form.name.trim(),
        label: form.label.trim(),
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        thumbnailImage: thumbnailUrl,
        images: imageUrls,
        operationTimes: opTimes.map((t) => ({
          name: t.name.trim(),
          startTime: toTimeString(t.startTime),
          endTime: toTimeString(t.endTime),
        })),
      });

      onSuccess();
    } catch (err: any) {
      setSubmitError("건물 추가에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Drawer onClick={(e) => e.stopPropagation()}>

        {/* 헤더 */}
        <S.DrawerHeader>
          <S.DrawerTitle>건물 추가</S.DrawerTitle>
          <S.CloseButton onClick={onClose}>✕</S.CloseButton>
        </S.DrawerHeader>

        {/* 바디 */}
        <S.DrawerBody>

          {/* 기본 정보 */}
          <S.Field>
            <S.Label htmlFor="name">
              건물명 <S.Required>*</S.Required>
            </S.Label>
            <S.Input
              id="name" name="name"
              placeholder="예) 7호관 전기컴퓨터관"
              value={form.name}
              onChange={handleChange}
              hasError={!!errors.name}
            />
            {errors.name && <S.FieldError>{errors.name}</S.FieldError>}
          </S.Field>

          <S.Field>
            <S.Label htmlFor="label">
              약칭 <S.Required>*</S.Required>
            </S.Label>
            <S.Input
              id="label" name="label"
              placeholder="예) 07"
              value={form.label}
              onChange={handleChange}
              hasError={!!errors.label}
            />
            {errors.label && <S.FieldError>{errors.label}</S.FieldError>}
          </S.Field>

          <S.FieldRow>
            <S.Field>
              <S.Label htmlFor="latitude">
                위도 <S.Required>*</S.Required>
              </S.Label>
              <S.Input
                id="latitude" name="latitude" type="number" step="any"
                placeholder="예) 37.123456"
                value={form.latitude}
                onChange={handleChange}
                hasError={!!errors.latitude}
              />
              {errors.latitude && <S.FieldError>{errors.latitude}</S.FieldError>}
            </S.Field>
            <S.Field>
              <S.Label htmlFor="longitude">
                경도 <S.Required>*</S.Required>
              </S.Label>
              <S.Input
                id="longitude" name="longitude" type="number" step="any"
                placeholder="예) 127.123456"
                value={form.longitude}
                onChange={handleChange}
                hasError={!!errors.longitude}
              />
              {errors.longitude && <S.FieldError>{errors.longitude}</S.FieldError>}
            </S.Field>
          </S.FieldRow>

          {/* 썸네일 이미지 */}
          <S.Field>
            <S.Label>
              썸네일 이미지 <S.Required>*</S.Required>
            </S.Label>
            <S.ImageUploadBox hasError={!!errors.thumbnail}>
              <input type="file" accept="image/*" onChange={handleThumbnail} />
              {thumbnailFile ? (
                <>
                  <S.UploadIcon>🖼️</S.UploadIcon>
                  <S.UploadedName>{thumbnailFile.name}</S.UploadedName>
                </>
              ) : (
                <>
                  <S.UploadIcon>📷</S.UploadIcon>
                  <S.UploadText>클릭해서 썸네일 이미지 선택</S.UploadText>
                </>
              )}
            </S.ImageUploadBox>
            {errors.thumbnail && <S.FieldError>{errors.thumbnail}</S.FieldError>}
          </S.Field>

          {/* 추가 이미지 */}
          <S.Field>
            <S.Label>추가 이미지</S.Label>
            {extraFiles.length > 0 && (
              <S.MultiImageList>
                {extraFiles.map((file, i) => (
                  <S.MultiImageRow key={i}>
                    <S.MultiImageName>{file.name}</S.MultiImageName>
                    <S.RemoveImageBtn onClick={() => removeExtraFile(i)}>
                      삭제
                    </S.RemoveImageBtn>
                  </S.MultiImageRow>
                ))}
              </S.MultiImageList>
            )}
            <S.AddImageBtn>
              <input
                ref={extraInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleExtraFiles}
              />
              + 이미지 추가
            </S.AddImageBtn>
          </S.Field>

          {/* 운영 시간 */}
          <S.Field>
            <S.SectionLabel>
              운영 시간 <S.Required>*</S.Required>
            </S.SectionLabel>
            <S.OpTimeList>
              {opTimes.map((row, i) => (
                <S.OpTimeRow key={i}>
                  <S.Field>
                    {i === 0 && <S.Label>구분명</S.Label>}
                    <S.Input
                      placeholder="예) 평일"
                      value={row.name}
                      onChange={(e) => handleOpTimeChange(i, "name", e.target.value)}
                      hasError={!!errors.operationTimes && !row.name.trim()}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label>시작 시간</S.Label>}
                    <TimeSelect
                      value={row.startTime}
                      onChange={(v) => handleOpTimeChange(i, "startTime", v)}
                      height={44}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label>종료 시간</S.Label>}
                    <TimeSelect
                      value={row.endTime}
                      onChange={(v) => handleOpTimeChange(i, "endTime", v)}
                      height={44}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label style={{ opacity: 0 }}>-</S.Label>}
                    <S.RemoveOpBtn
                      onClick={() => removeOpTime(i)}
                      disabled={opTimes.length === 1}
                    >
                      ✕
                    </S.RemoveOpBtn>
                  </S.Field>
                </S.OpTimeRow>
              ))}
            </S.OpTimeList>
            {errors.operationTimes && (
              <S.FieldError style={{ marginTop: 8 }}>{errors.operationTimes}</S.FieldError>
            )}
            <S.AddOpBtn onClick={addOpTime} style={{ marginTop: 10 }}>
              + 운영 시간 추가
            </S.AddOpBtn>
          </S.Field>

          {submitError && (
            <S.FieldError style={{ textAlign: "center" }}>{submitError}</S.FieldError>
          )}

        </S.DrawerBody>

        {/* 푸터 */}
        <S.DrawerFooter>
          <S.CancelButton onClick={onClose}>취소</S.CancelButton>
          <S.SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "등록 중..." : "건물 추가"}
          </S.SubmitButton>
        </S.DrawerFooter>

      </S.Drawer>
    </S.Overlay>
  );
};

export default AdminBuildingDrawer;
